/**
 * SENS DU PRIX SELON LE CANAL — S77, ADR-072 (Constitution art. 1-2).
 *
 * - Evenements : le producteur vend lui-meme sur son stand. Le prix stocke est
 *   le PRIX DE VENTE ; la commission (taux x prix, TVA 8,5 % comprise dedans,
 *   ADR-058 §5) est reglee apres coup. net + commission = prix.
 * - Paniers : c'est Manrina qui vend. Le prix stocke est le NET DU PRODUCTEUR
 *   (« votre prix est sanctuarise ») ; le prix client en est DEDUIT par le
 *   gross-up du serveur (ADR-047, `apps/backend/.../orders/commission.ts`) :
 *     client = net / (1 - taux x (1 + TVA vente) x 1,085)
 *   La commission (taux + TVA 8,5 % dessus) s'ajoute au net, elle ne le rogne
 *   jamais.
 *
 * Avant S77, le portail ecrivait un prix de vente au canal Paniers, que le
 * serveur majorait ensuite comme un net : la commission comptait deux fois.
 */

const TVA_COMMISSION = 0.085
export const centimes = (n) => Math.round((n + Number.EPSILON) * 100) / 100

/** Le prix stocke a ce canal est-il le net du producteur ? (Paniers) */
export const prixEstNet = (code) => code === 'TOURNEE'

/** Taux de TVA vente (0-1) d'un produit : 2,1 % si TAUX_2_1, sinon exonere. */
export const tvaVenteDe = (tvaCode) => (tvaCode === 'TAUX_2_1' ? 0.021 : 0)

const decomposer = (net, client) => {
  const com = centimes(client - net)
  return { net, com, tva: centimes(com - com / (1 + TVA_COMMISSION)), client }
}

/** Evenements : depuis le net vise, ou depuis le prix de vente. */
export const calcDepuisNet = (net, taux) => decomposer(net, centimes(net / (1 - taux)))
export const calcDepuisClient = (client, taux) => decomposer(centimes(client * (1 - taux)), centimes(client))

/** Paniers : prix client deduit du net (miroir de `decomposerVente`, mode CLIENT). */
const denomPaniers = (taux, tvaVente) => 1 - taux * (1 + tvaVente) * (1 + TVA_COMMISSION)
export const calcPaniersDepuisNet = (net, taux, tvaVente = 0) => {
  const d = denomPaniers(taux, tvaVente)
  return decomposer(centimes(net), d > 0 ? centimes(net / d) : centimes(net))
}
/** Paniers, saisie « mon prix de vente » : on en deduit le net, puis le prix exact. */
export const calcPaniersDepuisClient = (client, taux, tvaVente = 0) =>
  calcPaniersDepuisNet(centimes(client * denomPaniers(taux, tvaVente)), taux, tvaVente)

/**
 * Decomposition d'une saisie a un canal. `mode` : 'net' ou 'client'.
 * Renvoie { net, com, tva, client, stocke } — `stocke` = ce qu'il faut ecrire
 * en base (net au canal Paniers, prix de vente aux Evenements) — ou null.
 */
export function calculerCanal(code, mode, val, taux, tvaVente = 0) {
  if (!(val > 0)) return null
  const calc = prixEstNet(code)
    ? (mode === 'client' ? calcPaniersDepuisClient(val, taux, tvaVente) : calcPaniersDepuisNet(val, taux, tvaVente))
    : (mode === 'client' ? calcDepuisClient(val, taux) : calcDepuisNet(val, taux))
  return { ...calc, stocke: prixEstNet(code) ? calc.net : calc.client }
}

/** Libelle de la ligne commission du bloc de transparence. */
export const libelleCommission = (code, taux) => (prixEstNet(code)
  ? `Commission Manrina (${Math.round(taux * 100)} % + TVA 8,5 %)`
  : `Commission Manrina (${Math.round(taux * 100)} %, TVA comprise)`)

export const fmtEuros = (n) => `${(Math.round(n * 100) / 100).toFixed(2).replace('.', ',')} €`
